/* eslint-disable @next/next/no-sync-scripts */
import { useState } from 'react'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { QueryClient, QueryClientProvider, Hydrate } from 'react-query'
// import { Roboto, Noto_Sans_TC, DM_Sans } from 'next/font/google'
import { appWithTranslation } from 'next-i18next'
import { ClickToComponent } from 'click-to-react-component'

// Libs
import { wrapper } from '@/redux/store'
import { setRouterState } from '@/redux/reducers/router'

// Methods
import { getOgImage } from '@/methods/og'

// Components
import App from '@/components/pages/App'

// Styles
import '@/styles/main.scss'
import '@/styles/Icon/index.scss'
import '@/styles/motion/index.scss'
import 'react-modern-drawer/dist/index.css'
import 'react-toastify/dist/ReactToastify.css'
import 'swiper/css'
import 'swiper/css/pagination'

// Constants
import { isLiveMallClub } from '@/constants/systemType'
import { ROUTE_PATH } from '@/constants/routePath'
import { VALID_BRAND_NAME } from '@/constants/brand'

const clientConfigs = {
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: (failureCount: any, error: any) => {
        if (error.status === 401 && error.name !== 'FETCH_REFRESH_TOKEN' && error.name !== 'FETCH_USER_VERIFY') return true

        return false
      },
    },
  },
}

// TODO: 照理要用 next/font，但目前 next/font 跟 inline-react-svg 的 babel 設定有衝突，要再研究 */
// 目前用 _document link 的方式
// const roboto = Roboto({ subsets: ['latin'], weight: ['100', '300', '400', '500', '700', '900'] })
// const notoSansTC = Noto_Sans_TC({ subsets: ['latin'], weight: ['100', '300', '400', '500', '700', '900'] })
// const dMSans = DM_Sans({ subsets: ['latin'], weight: ['400', '500', '700'] })

// export default function App(props) {

const getFavicon = (brandName: string) => {
  // 如果是品牌頁面，不設定全域 favicon，讓頁面特定的 favicon 生效
  const isExternalBrand = Object.values(VALID_BRAND_NAME).includes(brandName)
  if (isExternalBrand) {
    return null
  }

  let result = '/favicon.ico'

  if (isLiveMallClub) {
    result = '/images/livemallclub/favicon.ico'
  }

  return result
}

const getSiteName = () => {
  let result = 'TutorABC Shop'

  if (isLiveMallClub) {
    result = 'Live Mall Club'
  }

  return result
}

const getOgFbPages = () => {
  let result = '102837472875865'

  if (isLiveMallClub) {
    result = ''
  }

  return result
}

const getRobots = () => {
  return 'noindex, nofollow, nosnippet, notranslate, noimageindex'
}

const getIsIndexable = (pathname: string) => {
  const indexablePaths = [ROUTE_PATH.LANDING_PAGE]

  return indexablePaths.some((path) => pathname.startsWith(path))
}

const MyApp = (props: AppProps) => {
  const [queryClient] = useState(() => new QueryClient(clientConfigs))
  const { router } = props
  const { pathname } = router
  const { brandName, dehydratedState } = props.pageProps

  return (
    // <main className={`${roboto.className} ${notoSansTC.className} ${dMSans.className}`}>
    <>
      <Head>
        <title>{getSiteName()}</title>
        {isLiveMallClub && !getIsIndexable(pathname) && <meta name='robots' key='robots' content={getRobots()} />}
        <meta name='title' key='meta-title' content={getSiteName()} />
        {(() => {
          const favicon = getFavicon(brandName)
          return favicon && <link rel='shortcut icon' href={favicon} />
        })()}
        <meta property='og:title' key='og-title' content={getSiteName()} />
        <meta property='og:type' key='type' content='website' />
        <meta property='og:site_name' key='site_name' content={getSiteName()} />
        <meta property='og:image:width' key='og:image:width' content='1200' />
        <meta property='og:image:height' key='og:image:height' content='628' />
        <meta property='og:image' key='og:image' content={getOgImage()} />
        <meta property='fb:pages' key='fb:pages' content={getOgFbPages()} />
        <meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no' />
        <script src='https://ditto-api.tutorabc.com/integrate/v1/location.js' />
      </Head>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={dehydratedState}>
          <App {...props} />
        </Hydrate>
      </QueryClientProvider>
      <ClickToComponent editor='cursor' />
    </>
  )
}

// Add getInitialProps to set initial router state for SSR/SSG
MyApp.getInitialProps = wrapper.getInitialAppProps((store) => async (context) => {
  const { ctx } = context
  const { pathname, query, asPath } = ctx

  // Set initial router state for SSR/SSG
  store.dispatch(
    setRouterState({
      pathname: pathname || '',
      query: (query || {}) as Record<string, string | string[]>,
      asPath: asPath || '',
    }),
  )

  return {
    pageProps: {},
  }
})

export default wrapper.withRedux(appWithTranslation(MyApp))
